import ClickableLink from 'components/Common/Clickable/ClickableLink'
import Image from 'components/Common/Image/Image'
import OfferRating from 'components/Common/NumberRating/OfferRating'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import PaletteSheet from 'LuxKit/PaletteSheet'
import BodyText from 'LuxKit/Typography/BodyText'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import config from 'constants/config'
import { rem } from 'polished'
import React, { useCallback, useContext } from 'react'
import styled from 'styled-components'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import { isString } from 'lib/string/stringUtils'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import OfferUrgencyCountdownLabel from 'components/Common/Labels/OfferUrgencyCountdownLabel'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import clsx from 'clsx'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import useHasMounted from 'hooks/useHasMounted'

export const CAROUSEL_CARD_WIDTH = 512

const CarouselCardContainer = styled(ClickableLink)`
  display: grid;
  grid-template-rows: ${rem(200)} 1fr;
  gap: ${rem(16)};
  height: 100%;
  position: relative;
  min-height: ${rem(520)};

  ${mediaQueryUp.desktop} {
    grid-template-rows: ${rem(280)} 1fr;
  }

  &.variant-opaque {
    background-color: ${props => props.theme.palette.neutral.default.eight};
  }
`

const ImageContainer = styled(PaletteSheet)`
  position: relative;

  &.hover {
    > :nth-child(2) {
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      right: 0;
      transition: opacity 0.3s;
      opacity: 0;
    }

    > :nth-child(1) {
      transition: opacity 0.3s;
      opacity: 1;
    }

    ${mediaHoverable} {
      &:hover {
        > :nth-child(1) {
          opacity: 0;
        }

        > :nth-child(2) {
          opacity: 1;
        }
      }
    }
  }
`

const ImageOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: grid;
  grid-template-columns: auto auto;
  grid-template-areas: "left right";
  grid-template-rows: 1fr;
  padding: ${rem(8)} ${rem(12)};
  pointer-events: none;

  & > .left {
    justify-self: flex-start;
    align-self: flex-start;
    grid-area: left;
  }

  & > .right {
    justify-self: flex-end;
    align-self: flex-start;
    grid-area: right;
  }

  a, button {
    pointer-events: auto;
  }
`

const DetailsContainer = styled(Group)`
  &.variant-opaque {
    padding: 0 ${rem(16)} ${rem(16)} ${rem(16)};
  }
`

interface Props extends Utils.PropsWithDataAttributes {
  /**
   * @default default
   */
  variant?: 'default' | 'opaque'
  image: App.Image
  images?: Array<App.Image>
  location: string | React.ReactElement
  title: string
  providerName?: string
  to: string
  className?: string
  priceDetails?: React.ReactElement
  bookmarkButton?: React.ReactElement
  urgencyLabels?: React.ReactElement
  imageCornerLabel?: React.ReactElement
  description?: string | React.ReactElement
  rating?: App.OfferRating
  onClick?: () => void
  timeRemainingLabel?: App.OfferUrgencyLabel
  partnershipLabel?: React.ReactElement
  loyaltyPointsElement?: React.ReactElement
  loyaltyPackageUpgradeElement?: React.ReactElement
  /**
   * Being used by cro_inclusion_chips_on_homepage_hero_carousels AB test (CRO-5502)
   */
  taglineInclusions?: React.ReactElement
  /**
   * Whether or not the offer tile should load it's image without
   * waiting for the tile to be in view.
   */
  eagerLoadImage?: boolean
}

function CarouselCardMedium(props: Props) {
  const {
    variant = 'default',
    bookmarkButton,
    image: baseImage,
    images,
    location,
    providerName,
    title: heading,
    priceDetails: offerPriceDetails,
    to: offerUrl,
    rating,
    urgencyLabels,
    className,
    description,
    timeRemainingLabel,
    partnershipLabel,
    imageCornerLabel,
    loyaltyPointsElement,
    loyaltyPackageUpgradeElement,
    taglineInclusions,
    eagerLoadImage,
    ...rest
  } = props

  const dispatchOfferListEvent = useContext(OfferListEventsContext)

  const handleImageLoaded = useCallback(() => {
    dispatchOfferListEvent({ type: OfferListEvents.offerReady })
  }, [dispatchOfferListEvent])

  const hoverImage = images?.[1]
  const isDesktop = useScreenSizeUp('tablet')
  const isMounted = useHasMounted()
  const isHoverEnabled = isMounted && isDesktop && !!hoverImage

  const image = baseImage ?? images?.[0]

  return (
    <CarouselCardContainer
      {...rest}
      target={config.OPEN_NEW_TAB_OFFER_CLICK ? '_blank' : undefined}
      to={offerUrl}
      className={clsx(className, `variant-${variant}`)}
    >
      <ImageContainer
        paletteType="default"
        className={clsx({ hover: isHoverEnabled })}
      >
        <Image
          aspectRatio="3:2"
          image={image}
          fit="center"
          dpr={2}
          height="280"
          width="512"
          onLoad={handleImageLoaded}
          loading={eagerLoadImage ? 'eager' : undefined}
        />
        {hoverImage && isHoverEnabled && (
          <Image
            aspectRatio="3:2"
            image={hoverImage}
            fit="center"
            dpr={2}
            height="280"
            width="512"
          />
        )}
        <ImageOverlay>
          <LabelGroup className="left">
            {timeRemainingLabel?.end && <OfferUrgencyCountdownLabel
              endDate={timeRemainingLabel.end}
              tagPlace="carousel-card-medium"
            />}
            {!timeRemainingLabel && imageCornerLabel}
            {partnershipLabel}
          </LabelGroup>
          <TripGuard>
            <div className="right">
              {bookmarkButton}
            </div>
          </TripGuard>
        </ImageOverlay>
      </ImageContainer>
      <DetailsContainer
        direction="vertical"
        verticalAlign="space-between"
        gap={12}
        className={`variant-${variant}`}
      >
        <VerticalSpacer gap={8}>
          <Group direction="horizontal" gap={8} horizontalAlign="space-between">
            <Group direction="vertical">
              {isString(location) ? <BodyText colour="neutral-one" variant="medium">{location}</BodyText> : location}
              {providerName && <BodyText colour="neutral-one" variant="medium" weight="bold">{providerName}</BodyText>}
            </Group>
            {rating && <OfferRating
              variant="xs"
              rating={rating}
              onlyNumber="auto"
              inlineLabel
              hideLink
            />}
          </Group>
          <Group direction="vertical" gap={4}>
            <BodyText
              colour="neutral-one"
              variant="large"
              weight="bold"
              lineClamp={3}
              desktopLineClamp={2}
            >
              {heading}
            </BodyText>
            {taglineInclusions}
          </Group>
          {description && isString(description) && <BodyText colour="neutral-two" variant="medium">{description}</BodyText>}
          {description && !isString(description) && description}
          {loyaltyPackageUpgradeElement}
          {loyaltyPointsElement}
          {urgencyLabels}
        </VerticalSpacer>
        <div>
          {offerPriceDetails}
        </div>
      </DetailsContainer>
    </CarouselCardContainer>
  )
}

export default CarouselCardMedium
